import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Pagination,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { hasUserRole } from "../../admin/account/userRoles";
import { ACCENT, TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import TutorsWorkspaceShell from "./TutorsWorkspaceShell";
import useTutorHub from "./useTutorHub";
import {
    TUTOR_DETAILS_KEYS,
    groupBreadcrumb,
    groupsForLibraryItem,
    newTutorId,
    readTutorGroups,
    readTutorLibrary,
    readTutorLibraryAll,
    titleFromSourceText,
    withDeletedStories,
} from "./tutorHubDetails";

const TUTOR_LIBRARY_LIMIT = 100;
const LIBRARY_PAGE_SIZE = 15;

function todayStoryCode() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${month}${day}`;
}

function nextStorySequence(library, dayCode) {
    const prefix = `${dayCode}-`;
    let max = 0;
    library.forEach((item) => {
        const title = String(item?.title || "");
        if (!title.startsWith(prefix)) {
            return;
        }
        const sequence = Number(title.slice(prefix.length));
        if (Number.isFinite(sequence) && sequence > max) {
            max = sequence;
        }
    });
    return max + 1;
}

const TEST_STORY_CONTENT = `Dr. Helena Marsh had devoted twenty years to studying the fragrance patterns of rare orchids at the botanical garden, meticulously documenting each petal, sepal, and stamen in her laboratory notebooks. When the prized Lynx Orchid—named for its spotted corolla—vanished three days before the international symposium, everyone assumed a rival researcher had stolen it. Helena examined the empty pedestal, noting the clean cut at the peduncle and the absence of any cellular debris at the receptacle scar. The security footage showed her own graduate student, Marcus, entering the greenhouse late that night.

But something felt wrong. Helena reviewed Marcus's dissertation on pistil development and ovary structure, then cross-referenced the timestamps. At the exact moment the camera showed "Marcus" near the Lynx Orchid, she had texted him about dehiscence rates in the control group—and he'd responded immediately with detailed data from the electron microscope across campus. Someone had staged the theft to stop the symposium, someone who understood botanical anatomy well enough to fake the extraction.

Helena confronted the symposium organizer, Dr. Voss, presenting the alibi and her own discovery: microscopic analysis revealed the cut was made with surgical precision at the exact point where the pedicel meets the stem, a technique Voss himself had pioneered. He'd stolen his own prized specimen for the insurance payout, planning to "recover" it after the symposium ended. His face went pale as Helena revealed the tiny camera she'd hidden among the whorl of leaves in the adjacent planter—footage showing Voss carefully excising the flower while wearing a Marcus costume.

The real twist came when Voss laughed bitterly and opened his jacket. Tucked inside was the Lynx Orchid, its radial symmetry perfect, its ovules intact. "I wasn't stealing it," he said quietly. "I was saving it. The university made a deal with developers—this greenhouse ends next month. They're putting in a parking pool and luxury condos. I staged the theft to create an international incident, force media attention, make them stop the demolition. I would've taken the fall, but I couldn't let this species go extinct." Helena stood at the apex of an impossible choice: turn in a thief or save a sanctuary. She looked at the orchid's delicate anther, then at the demolition notice tucked behind the calyx of permits on Voss's desk. "Then we'd better make this scandal big enough to matter," she said, reaching for her phone to call every journalist she knew.`;

function buildTestStory(dayCode, sequence) {
    const code = `${dayCode}-${String(sequence).padStart(2, "0")}`;
    return {
        id: newTutorId(),
        title: code,
        sourceText: TEST_STORY_CONTENT,
        createdAt: new Date().toISOString(),
        status: "ACTIVE",
    };
}

export default function TutorsLibraryPage() {
    const { t } = usePagesTranslation();
    const location = useLocation();
    const { cloudUser, details, loading, saving, error, patchDetails } = useTutorHub();
    const [sourceText, setSourceText] = useState("");
    const [title, setTitle] = useState("");
    const [editingId, setEditingId] = useState(null);
    const formRef = useRef(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [capacityOpen, setCapacityOpen] = useState(false);
    const [pendingStory, setPendingStory] = useState(null);
    const library = useMemo(() => readTutorLibrary(details), [details]);
    const groups = useMemo(() => readTutorGroups(details), [details]);
    const guideSeen = Boolean(details?.[TUTOR_DETAILS_KEYS.guideSeen]);
    const isAdmin = hasUserRole(cloudUser, "ADMIN");
    const [page, setPage] = useState(1);
    const pageCount = Math.max(1, Math.ceil(library.length / LIBRARY_PAGE_SIZE));
    const currentPage = Math.min(page, pageCount);
    const pageStories = library.slice(
        (currentPage - 1) * LIBRARY_PAGE_SIZE,
        currentPage * LIBRARY_PAGE_SIZE,
    );

    useEffect(() => {
        if (location.hash !== "#create" || loading) {
            return;
        }
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [location.hash, loading]);

    const addTestStories = async (count) => {
        if (!isAdmin || saving || count < 1) {
            return;
        }
        const dayCode = todayStoryCode();
        let sequence = nextStorySequence(library, dayCode);
        const room = TUTOR_LIBRARY_LIMIT - library.length;
        if (room <= 0) {
            setPendingStory(buildTestStory(dayCode, sequence));
            setCapacityOpen(true);
            return;
        }
        const stories = [];
        const toAdd = Math.min(count, room);
        for (let i = 0; i < toAdd; i += 1) {
            stories.push(buildTestStory(dayCode, sequence));
            sequence += 1;
        }
        await persistLibrary(
            [...stories, ...library],
            { [TUTOR_DETAILS_KEYS.guideSeen]: true },
        );
        setPage(1);
    };

    const removeTestStories = async (count) => {
        if (!isAdmin || saving || count < 1 || library.length === 0) {
            return;
        }
        const removeIds = new Set(library.slice(0, count).map((item) => item.id));
        const nextLibrary = readTutorLibraryAll(details).map((item) => (
            removeIds.has(item.id) ? { ...item, status: "DELETED", pdfUrl: "" } : item
        ));
        const nextGroups = groups.map((group) => ({
            ...group,
            puzzleIds: group.puzzleIds.filter((puzzleId) => !removeIds.has(puzzleId)),
        }));
        await patchDetails({
            [TUTOR_DETAILS_KEYS.library]: nextLibrary,
            [TUTOR_DETAILS_KEYS.groups]: nextGroups,
        });
        setPage(1);
    };

    const persistLibrary = async (nextVisible, extra = {}) => {
        await patchDetails({
            [TUTOR_DETAILS_KEYS.library]: withDeletedStories(details, nextVisible),
            ...extra,
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setTitle("");
        setSourceText("");
    };

    const startEdit = (item) => {
        setEditingId(item.id);
        setTitle(item.title || "");
        setSourceText(item.sourceText || "");
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleSave = async (event) => {
        event.preventDefault();
        const trimmed = sourceText.trim();
        if (!trimmed || saving) {
            return;
        }
        const nextTitle = title.trim() || titleFromSourceText(trimmed);
        if (editingId) {
            const current = library.find((item) => item.id === editingId);
            if (!current) {
                cancelEdit();
                return;
            }
            const textChanged = current.sourceText !== trimmed;
            await persistLibrary(
                library.map((item) => (
                    item.id === editingId
                        ? {
                            ...item,
                            title: nextTitle,
                            sourceText: trimmed,
                            ...(textChanged ? { clues: [], pdfUrl: "" } : {}),
                        }
                        : item
                )),
                { [TUTOR_DETAILS_KEYS.guideSeen]: true },
            );
            cancelEdit();
            return;
        }
        const story = {
            id: newTutorId(),
            title: nextTitle,
            sourceText: trimmed,
            createdAt: new Date().toISOString(),
            status: "ACTIVE",
        };
        if (library.length >= TUTOR_LIBRARY_LIMIT) {
            setPendingStory(story);
            setCapacityOpen(true);
            return;
        }
        await persistLibrary(
            [story, ...library],
            { [TUTOR_DETAILS_KEYS.guideSeen]: true },
        );
        setSourceText("");
        setTitle("");
        setPage(1);
    };

    const closeCapacity = () => {
        if (saving) {
            return;
        }
        setCapacityOpen(false);
        setPendingStory(null);
    };

    const handleCapacityDelete = async (itemId) => {
        if (!itemId || !pendingStory || saving) {
            return;
        }
        const nextLibrary = readTutorLibraryAll(details).map((item) => (
            item.id === itemId ? { ...item, status: "DELETED", pdfUrl: "" } : item
        ));
        const nextGroups = groups.map((group) => ({
            ...group,
            puzzleIds: group.puzzleIds.filter((puzzleId) => puzzleId !== itemId),
        }));
        await patchDetails({
            [TUTOR_DETAILS_KEYS.library]: [
                { ...pendingStory, status: "ACTIVE" },
                ...nextLibrary,
            ],
            [TUTOR_DETAILS_KEYS.groups]: nextGroups,
            [TUTOR_DETAILS_KEYS.guideSeen]: true,
        });
        setSourceText("");
        setTitle("");
        setPendingStory(null);
        setCapacityOpen(false);
        setPage(1);
    };

    const handleDelete = async () => {
        const itemId = pendingDelete?.id;
        if (!itemId) {
            return;
        }
        const nextLibrary = readTutorLibraryAll(details).map((item) => (
            item.id === itemId ? { ...item, status: "DELETED", pdfUrl: "" } : item
        ));
        const nextGroups = groups.map((group) => ({
            ...group,
            puzzleIds: group.puzzleIds.filter((puzzleId) => puzzleId !== itemId),
        }));
        await patchDetails({
            [TUTOR_DETAILS_KEYS.library]: nextLibrary,
            [TUTOR_DETAILS_KEYS.groups]: nextGroups,
        });
        setPendingDelete(null);
        if (itemId === editingId) {
            cancelEdit();
        }
    };

    return (
        <TutorsWorkspaceShell
            title={t("tutors.library-title", "Story library")}
            loading={loading}
            error={error}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                {t("tutors.library-count", {
                    count: library.length,
                    limit: TUTOR_LIBRARY_LIMIT,
                    defaultValue: "{{count}} / {{limit}} stories",
                })}
            </Typography>
            <Typography variant="body1" sx={{ color: TEXT.secondary, lineHeight: 1.7, mb: 2 }}>
                {t(
                    "tutors.library-body",
                    "Your puzzles only. Paste or write text, then we draft a crossword so you can place some of the words. Crossword only — private to you.",
                )}
            </Typography>

            <Accordion defaultExpanded={!guideSeen} sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                        {t("tutors.library-guide-title", "How to make a crossword")}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ color: TEXT.secondary, mb: 1 }}>
                        {t("tutors.library-guide-1", "1. Paste or write the lesson text in the box.")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT.secondary, mb: 1 }}>
                        {t("tutors.library-guide-2", "2. Save it here. We keep the text private and will draft a crossword from it.")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT.secondary }}>
                        {t("tutors.library-guide-3", "3. Place some of the words with the fill tool, then attach the puzzle to a group and download a PDF.")}
                    </Typography>
                </AccordionDetails>
            </Accordion>

            <Box id="create" ref={formRef} component="form" onSubmit={handleSave} sx={{ pt: 3, mb: 3 }}>
                <TextField
                    label={t("tutors.library-field-title", "Title")}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    fullWidth
                    sx={{ mb: 1.5 }}
                />
                <TextField
                    label={t("tutors.library-paste", "Paste or write text")}
                    value={sourceText}
                    onChange={(event) => setSourceText(event.target.value)}
                    multiline
                    minRows={6}
                    fullWidth
                    sx={{ mb: 1.5 }}
                />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={saving || !sourceText.trim()}
                        sx={{ minHeight: 44, textTransform: "none", fontWeight: 700, bgcolor: ACCENT.main }}
                    >
                        {editingId
                            ? t("tutors.library-save-edit", "Save changes")
                            : t("tutors.library-save", "Save to library")}
                    </Button>
                    {editingId ? (
                        <Button
                            type="button"
                            onClick={cancelEdit}
                            disabled={saving}
                            sx={{ minHeight: 44, textTransform: "none" }}
                        >
                            {t("btn.cancel", "Cancel")}
                        </Button>
                    ) : null}
                </Box>
            </Box>

            {library.length === 0 ? (
                <Typography color="text.secondary">
                    {t("tutors.library-empty", "No private crosswords yet. Paste a short text to start.")}
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {pageStories.map((item) => {
                        const assigned = groupsForLibraryItem(groups, item.id);
                        return (
                        <Card key={item.id} sx={{ p: 2.5 }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{item.title}</Typography>
                                    {assigned.length === 0 ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                                            {t("tutors.library-no-group", "No group")}
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
                                            {assigned.map((group) => (
                                                <Chip
                                                    key={group.id}
                                                    size="small"
                                                    label={groupBreadcrumb(groups, group.id) || group.name}
                                                    component={RouterLink}
                                                    to="/tutors"
                                                    clickable
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                                        {item.sourceText.length > 280 ? `${item.sourceText.slice(0, 277)}…` : item.sourceText}
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                        <Button
                                            component={RouterLink}
                                            to={`/tutors/library/${item.id}/play`}
                                            size="small"
                                            variant="contained"
                                            sx={{ minHeight: 44, textTransform: "none", fontWeight: 700, bgcolor: ACCENT.main }}
                                        >
                                            {t("tutors.play-open", "Play")}
                                        </Button>
                                        <Button
                                            component={RouterLink}
                                            to="/tutors/worksheets"
                                            size="small"
                                            sx={{ minHeight: 44, textTransform: "none" }}
                                        >
                                            {t("nav.tutors-worksheets", "Worksheet generator")}
                                        </Button>
                                    </Box>
                                </Box>
                                <IconButton
                                    aria-label={t("btn.edit", "Edit")}
                                    onClick={() => startEdit(item)}
                                    disabled={saving}
                                    sx={{ minWidth: 44, minHeight: 44 }}
                                >
                                    <EditOutlinedIcon />
                                </IconButton>
                                <IconButton
                                    aria-label={t("btn.delete", "Delete")}
                                    onClick={() => setPendingDelete(item)}
                                    disabled={saving}
                                    sx={{ minWidth: 44, minHeight: 44 }}
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Box>
                        </Card>
                        );
                    })}
                </Stack>
            )}
            {pageCount > 1 ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Pagination
                        count={pageCount}
                        page={currentPage}
                        onChange={(_event, value) => setPage(value)}
                        color="primary"
                        shape="rounded"
                        sx={{ "& .MuiPaginationItem-root": { minWidth: 44, minHeight: 44 } }}
                    />
                </Box>
            ) : null}
            <Dialog
                open={Boolean(pendingDelete)}
                onClose={() => {
                    if (!saving) {
                        setPendingDelete(null);
                    }
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>{pendingDelete?.title}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ lineHeight: 1.7 }}>
                        {t(
                            "tutors.library-delete-confirm",
                            "Would you like to delete this? This action can't be undone. The PDF file will be removed too.",
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setPendingDelete(null)}
                        disabled={saving}
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        {t("btn.cancel", "Cancel")}
                    </Button>
                    <Button
                        onClick={handleDelete}
                        disabled={saving}
                        color="error"
                        variant="contained"
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        {t("btn.delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={capacityOpen}
                onClose={closeCapacity}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {t("tutors.library-full-title", {
                        count: library.length,
                        limit: TUTOR_LIBRARY_LIMIT,
                        defaultValue: "Library Full ({{count}}/{{limit}} Saved)",
                    })}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ lineHeight: 1.7, mb: 2 }}>
                        {t(
                            "tutors.library-full-body",
                            "Delete a saved puzzle to store this one. Your new text stays in the form until it is saved.",
                        )}
                    </Typography>
                    <Box sx={{ maxHeight: 360, overflowY: "auto", scrollbarGutter: "stable", pr: 2 }}>
                        {library.map((item) => (
                            <Box
                                key={item.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    py: 1,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 600 }} noWrap>
                                    {item.title}
                                </Typography>
                                <Button
                                    color="error"
                                    variant="outlined"
                                    disabled={saving}
                                    onClick={() => handleCapacityDelete(item.id)}
                                    sx={{ minHeight: 44, flexShrink: 0, textTransform: "none" }}
                                >
                                    {t("btn.delete", "Delete")}
                                </Button>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={closeCapacity}
                        disabled={saving}
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        {t("btn.cancel", "Cancel")}
                    </Button>
                </DialogActions>
            </Dialog>
            {isAdmin ? (
                <Box sx={{ mt: 4 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        These buttons show up because of admin
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            disabled={saving}
                            onClick={() => addTestStories(90)}
                            sx={{ minHeight: 44, textTransform: "none", fontWeight: 700 }}
                        >
                            Add 90 stories
                        </Button>
                        <Button
                            variant="outlined"
                            disabled={saving}
                            onClick={() => addTestStories(1)}
                            sx={{ minHeight: 44, textTransform: "none", fontWeight: 700 }}
                        >
                            Add random story
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={saving || library.length === 0}
                            onClick={() => removeTestStories(20)}
                            sx={{ minHeight: 44, textTransform: "none", fontWeight: 700 }}
                        >
                            Remove 20 stories
                        </Button>
                    </Box>
                </Box>
            ) : null}
        </TutorsWorkspaceShell>
    );
}
